/**
 * ค่าตั้งของแอป — เก็บ localStorage (เล็ก อ่านตอน boot ไม่ต้อง async)
 * ความคืบหน้าการเรียนอยู่ใน IndexedDB ไม่ปนกัน
 */
import type { DeckId } from '@/core/types'
import type { ModeId } from '@/decks/types'
import { FONTS } from './fonts'

const KEY = 'jpt-settings-v1'

export interface Settings {
  deck: DeckId
  /** โหมดที่เลือกไว้ของแต่ละ deck */
  mode: Partial<Record<DeckId, ModeId>>
  /**
   * การ์ดที่เลือกไว้ของแต่ละ deck — ว่าง = ทั้งหมด (ยกเว้นที่แท็ก rare)
   * เคยมีชิปกรองคู่กันด้วย แต่ตัดออกแล้ว: กลไกเดียวเข้าใจง่ายกว่าสองอันที่ทับกัน
   */
  picked: Partial<Record<DeckId, string[]>>
  /** โหมดเก็บตก — ฝึกเฉพาะตัวที่ยังไม่แน่น */
  weakOnly: boolean
  /**
   * โชว์คำอ่าน on/kun ตอนเฉลย
   * NOTES.md §9 แนะให้แยกจำความหมายกับคำอ่านคนละรอบ — ปิดได้ถ้าอยากทำตามนั้นเป๊ะ
   */
  showReadings: boolean
  fonts: string[]
  keypad: boolean | null // null = ตัดสินจากอุปกรณ์
  theme: 'auto' | 'light' | 'dark'
  /** เล่นเสียงอัตโนมัติหลังเฉลย */
  autoAudio: boolean
  /** ใบใหม่ต่อวัน (SRS) */
  newPerDay: number
  /** ตัวคูณเวลาต่อขีดตอนวาดลำดับขีด — มากกว่า 1 คือช้าลง */
  strokeSpeed: number
  /** วาดลำดับขีดเองในโหมดเรียน — คู่กับ autoAudio */
  autoStroke: boolean
  /** โชว์เลขกำกับขีด — ปิดไว้เพราะ 14 เลขบนตัวเดียวอ่านไม่ทัน */
  strokeNumbers: boolean
  /** ที่อยู่ Worker สำหรับซิงก์ — ว่าง = ไม่ซิงก์ */
  syncUrl: string
  /** รหัสลับที่ตั้งไว้ตอน deploy Worker */
  syncKey: string
  /** ซิงก์ล่าสุดเมื่อไร (epoch ms) */
  syncedAt: number
}

const DEFAULTS: Settings = {
  deck: 'kana',
  mode: {},
  picked: {},
  weakOnly: false,
  showReadings: true,
  fonts: FONTS.map((f) => f.id),
  keypad: null,
  theme: 'auto',
  autoAudio: true,
  newPerDay: 15,
  strokeSpeed: 1,
  autoStroke: true,
  strokeNumbers: false,
  syncUrl: '',
  syncKey: '',
  syncedAt: 0,
}

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { ...DEFAULTS }
    const parsed = JSON.parse(raw) as Partial<Settings>
    return { ...DEFAULTS, ...parsed }
  } catch {
    return { ...DEFAULTS }
  }
}

export const settings: Settings = read()

export function saveSettings(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings))
  } catch {
    /* โหมดส่วนตัวของ Safari เขียนไม่ได้ — ไม่ใช่เรื่องคอขาดบาดตาย */
  }
}

export function getPicked(deck: DeckId): Set<string> {
  return new Set(settings.picked[deck] ?? [])
}

export function setPicked(deck: DeckId, ids: Set<string>): void {
  settings.picked[deck] = [...ids]
  saveSettings()
}

export function applyTheme(): void {
  const root = document.documentElement
  if (settings.theme === 'auto') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', settings.theme)
}

export function cycleTheme(): Settings['theme'] {
  const order: Array<Settings['theme']> = ['auto', 'light', 'dark']
  const i = order.indexOf(settings.theme)
  settings.theme = order[(i + 1) % order.length] as Settings['theme']
  applyTheme()
  saveSettings()
  return settings.theme
}

export const THEME_ICON: Record<Settings['theme'], string> = {
  auto: '◐',
  light: '☀',
  dark: '☾',
}
